import { Button, Card } from '../../../../components';
import { useUrlParams } from '../../../../hooks';
import { router, routes } from '../../../../app/routes';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { BookingEventModel } from '../../../../data/entities/booking/BookingEventModel';
import { AppointmentItem } from '../../components';
import { BookingWithDetails } from '../../../../models/booking/BookingWithDetails';
import { InvoiceModel } from '../../../../data/entities';
import { formatPhone } from '../../../../utils';


export function BookingConfirmationPage() {
  const locationCode = useUrlParams("locationCode");
  const location = useLocation();
  const state = location.state as { booking: BookingWithDetails; invoice: InvoiceModel };
  const booking = state?.booking;
  const invoice = state?.invoice;

  const handleNewBooking = () => {
    router.navigateWithParams(
      routes.book.index,
      new Map([["locationCode", locationCode]])
    );
  };

  if(!booking) return <></>

  return (
    <motion.div
      initial={{ y: "10px", opacity: 0 }}
      animate={{y: 0, opacity: 1 }}
      exit={{ y: "50%", opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut"}}
    >
      <div className="container mt-5" style={{ maxWidth: '800px', paddingBottom: '80px' }}>
        {/* Success Header */}
        <div className="text-center mb-5">
          <div className="mb-4">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3" 
                 style={{ width: '80px', height: '80px', backgroundColor: '#d4edda' }}>
              <i className="fa fa-check fa-3x" style={{ color: '#28a745' }}></i>
            </div>
          </div>
          <h1 className="mb-3">Booking Confirmed!</h1>
          <p className="text-muted mb-1">Thank you for your booking. We've sent a confirmation email to</p>
          <p className="fw-semibold text-primary mb-3">{booking.customer?.email}</p>
          <p className="text-muted small">
            <i className="fa fa-info-circle me-2"></i>
            If you don't see the email, please check your spam or junk folder
          </p>
        </div>

        {/* Customer Details Card */}
        <Card className="mb-4 shadow-sm border-0">
          <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
            <h5 className="mb-0">
              <i className="fa fa-user me-2"></i>
              Your Information
            </h5>
          </div>
          <div className="p-4">
            <div className="mb-3">
              <span className="text-muted small d-block mb-1">Name</span>
              <span className="fw-semibold">{booking.customer?.firstName} {booking.customer?.lastName}</span>
            </div>
            <div className="mb-3">
              <span className="text-muted small d-block mb-1">Email</span>
              <span className="fw-semibold">{booking.customer?.email}</span>
            </div>
            <div className="mb-0">
              <span className="text-muted small d-block mb-1">Phone</span>
              <span className="fw-semibold">{formatPhone(booking.customer?.phone)}</span>
            </div>
          </div>
        </Card>

        {/* Location Card */}
        {booking.location && (
          <Card className="mb-4 shadow-sm border-0">
            <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
              <h5 className="mb-0">
                <i className="fa fa-map-marker me-2"></i>
                Location
              </h5>
            </div>
            <div className="p-4">
              <p className="mb-1 fw-semibold">{booking.location.name}</p>
              <p className="mb-1 text-muted small">{booking.location.address?.streetAddress}</p>
              <p className="mb-0 text-muted small">
                {booking.location.address?.city}, {booking.location.address?.region?.abbreviation} {booking.location.address?.postalCode}
              </p>
            </div>
          </Card>
        )}

        {/* Appointments Card */}
        <Card className="mb-4 shadow-sm border-0">
          <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
            <h5 className="mb-0">
              <i className="fa fa-calendar me-2"></i>
              Appointment Details
            </h5>
          </div>
          <div className="p-0">
             {booking.bookingEvents.map((event: BookingEventModel, index: number) => (
              <AppointmentItem 
                key={event.id} 
                event={event} 
                bookingDate={event.date}
                invoice={invoice}
                eventIndex={index}
                isLast={index === booking.bookingEvents.length - 1}
              />
            ))}
         </div>
        </Card>

        {/* Important Notice */}
        <Card className="mb-4 shadow-sm">
          <div className="p-3">
            <div className="d-flex align-items-start">
              <i className="fa fa-exclamation-triangle me-3 mt-1" style={{ color: '#856404' }}></i>
              <div>
                <p className="mb-1 fw-semibold" style={{ color: '#856404' }}>Important Reminder</p>
                <p className="mb-0 small text-muted">
                  Please arrive 10 minutes early for your appointment. If you need to cancel or reschedule, 
                  please contact us at least 24 hours in advance.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="text-center">
          <Button variant="primary" className="px-5 py-2" onClick={handleNewBooking}>
            <i className="fa fa-plus me-2"></i>
            Book Another Appointment
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default BookingConfirmationPage;